import React, { useEffect, useMemo, useState } from 'react'
import SVG from 'react-inlinesvg'
import { Placeholder } from './Placeholder'
import {
  AssigneeList,
  formatStat,
  Header,
  isBlank,
  Loadable,
  map,
  NoDataDiv,
  Pagination,
  Select,
  TableSearch,
  useApiGet,
  useModal,
  usePagination,
  useSnackbar,
  varClass,
} from '../../shared'
import { getRequest } from '../../../services'
import { SuppressModal } from './SuppressModal'
import { SuppressionsView } from './SuppressionsView'
import {
  authorName,
  deleteSuppression,
  fetchTrackers,
  formatDate,
  ICommissionSuppression,
  ICommissionTracker,
  isActiveSuppression,
  setTrackerPriority,
  SUPPRESSIONS_PATH,
} from './api'

const PATH = '/discovers/v1/commissions/opportunities'
const CARDS_PER_PAGE = 20

// Client-side: these change which answer you read first, not the question asked of the database. So the
// backend's tiebreakers no longer govern DISPLAY order — they still govern the server-side cut.
const SORT_OPTIONS = [
  { value: 'clicks', label: 'Baseline clicks' },
  { value: 'gap', label: 'Rate gap' },
  { value: 'movable', label: 'Potential uplift' },
]
const MODEL_OPTIONS = [
  { value: 'all', label: 'All models' },
  { value: 'CPA', label: 'CPA' },
  { value: 'CPL', label: 'CPL' },
]
// Fixed, so a column is the same width on every card. Each figure clears that column's MEASURED maximum,
// taken with every row expanded. Change one and re-measure: under a fixed layout a column a few pixels
// short does not scroll, it wraps or overlaps its neighbour.
const COLUMN_WIDTHS = [
  '29%', // Move (from -> to): 419px against a 406px worst case, padding included
  '10%', // Scope: 150px against 115px for a subdomain scope like `uk.gymshark.com`, which is not trimmed
  '5%', // Model
  '9%', // Baseline rate -- also holds "Not prioritised" on the per-market rows
  '9%', // Candidate rate -- same
  '10%', // Gap -- carries the CPL currency caveat, so it needs more than the rates
  '12%', // Baseline clicks
  '16%', // Potential uplift
]
// Edit mode: the rate columns widen to fit a switch with its programme line. Sums to 100.
const COLUMN_WIDTHS_EDIT = ['22%', '8%', '5%', '16%', '16%', '8%', '11%', '14%']
// Mirrors the endpoint's Joi `.max(50)` on `excludeNetworkUuids`. Exceeding it server-side is a 400, which
// `useApiGet` stores as `{}` and this page renders as "Could not load the comparison" -- the same shape a
// permanent 500 takes, so the cap is enforced here rather than discovered there.
const MAX_EXCLUDED_NETWORKS = 50
// Excluded as CANDIDATES by default because their rates are not negotiable leads. Hardcoded uuids rather
// than a match on `networkName`: the picker list is a separate async
// fetch that is null on first render, and a rename would silently empty the defaults. Network uuids already
// travel in the query string, the response body and the picker, so this is not the account-uuid hygiene
// case. The labels are the FALLBACK name source for the chips, for a network absent from both picker sources.
const DEFAULT_EXCLUDED_NETWORKS = [
  { value: 'd6cb0636-d99d-459b-9161-60295c9d0729', label: 'Digidip' },
  { value: 'cbb013bb-37e6-4c80-b983-c10ddbb7f01b', label: 'Kelkoo' },
  { value: '0cc7be71-8e68-43d0-8477-bf58a1350971', label: 'SaleGains' },
]

// The payload is the contract, not this page's prose: see hlt-backend
// `src/apis/outbound/v1/discovers/commissions/controllers/commissions.controllers.interface.ts`
// (`IQueryCommissionOpportunitiesResponse`) and `src/app/trackers/v1/commission.grid/common.interface.ts`
// (`ICommissionOpportunityRow`). camelCase throughout, because every SingleStore knex instance uses
// `knexSnakeCaseMappers()`.

export const NetworkCommissions = (props) => {
  // `undefined` on either picker means "open", which is the parameter being OMITTED, not sent empty.
  const [baselineNetworkUuid, setBaselineNetworkUuid] = useState(undefined)
  const [candidateNetworkUuid, setCandidateNetworkUuid] = useState(undefined)
  // Not persisted. CANDIDATE side only: an excluded network is still eligible as the measured incumbent.
  const [excludeNetworkUuids, setExcludeNetworkUuids] = useState(
    DEFAULT_EXCLUDED_NETWORKS.map((network) => network.value)
  )
  // `undefined` means "omitted", which is the only way the backend's mode-conditional default fires
  // (0 with a baseline named, 100 with both open). `changeBaseline` resets it for the same reason.
  const [minClicks, setMinClicks] = useState(undefined)
  // Not in the request deps: both are applied to what came back. See `sortOptions`.
  // Uplift by default, which is also the order the backend ranks and takes its 5,000-row cut in — so
  // the page agrees with how the capped set was chosen. Rows with no uplift keep the server's order
  // behind the scored ones, since the comparator sorts nulls last and Array.sort is stable.
  const [sortBy, setSortBy] = useState('movable')
  const [modelGroup, setModelGroup] = useState('all')
  // Separate from the request value: without it, typing "1500" fired four requests at 4.57 s each.
  const [minClicksDraft, setMinClicksDraft] = useState(undefined)
  // Lives HERE, not in `Content`, which unmounts on every deps change and would lose what was typed.
  const [search, setSearch] = useState('')
  useEffect(() => {
    if (minClicksDraft === minClicks) return undefined
    const timer = setTimeout(() => setMinClicks(minClicksDraft), 400)
    return () => clearTimeout(timer)
    // `minClicks` is read above, so it belongs here: omitting it only worked because `changeBaseline`
    // and `swap` happen to write both values together.
  }, [minClicksDraft, minClicks])
  // The only way back from the error state: a failed request leaves `useApiGet` holding `{}` forever and
  // refetches only on a deps change.
  const [reloadToken, setReloadToken] = useState(0)

  // The sibling Discover pages already fetch this and `memostorage` whitelists it, so this is free here.
  const networkList = useApiGet(`/networks/v1/list`)

  // One fetch feeds both tabs, refetched after every suppress / unsuppress.
  const [tab, setTab] = useState<'opportunities' | 'suppressions'>('opportunities')
  const [suppressionsKey, setSuppressionsKey] = useState(0)
  const suppressionsData: any = useApiGet(
    SUPPRESSIONS_PATH,
    { includeInactive: 'true' },
    [suppressionsKey]
  )
  const suppressions: ICommissionSuppression[] =
    suppressionsData?.suppressions || []
  const activeSuppressions = useMemo(
    () => suppressions.filter(isActiveSuppression),
    [suppressions]
  )
  const refetchSuppressions = () => setSuppressionsKey((key) => key + 1)

  // Undefined keys are OMITTED: `URLSearchParams` stringifies `undefined` to "undefined", which the
  // endpoint's `.guid()` then 400s. Nothing upstream strips them.
  const params: any = {}
  if (baselineNetworkUuid) params.baselineNetworkUuid = baselineNetworkUuid
  if (candidateNetworkUuid) params.candidateNetworkUuid = candidateNetworkUuid
  if (minClicks !== undefined) params.minClicks = minClicks
  // Omitted when EMPTY rather than sent as '': the endpoint splits and validates each element, so ''
  // arrives as `['']` and 400s.
  const excludeParam = excludeNetworkUuids.join(',')
  if (excludeParam) params.excludeNetworkUuids = excludeParam

  // The THIRD POSITIONAL argument of `useApiGet(path, params, deps)`, not an options key.
  const data: any = useApiGet(PATH, params, [
    baselineNetworkUuid,
    candidateNetworkUuid,
    minClicks,
    // The JOINED STRING, never the array: deps compare by identity, so a literal refetches forever.
    excludeParam,
    reloadToken,
  ])

  const changeBaseline = (option) => {
    setBaselineNetworkUuid(option ? option.value : undefined)
    // Re-apply the mode's default by omitting the parameter again: an input still holding 100 after a
    // baseline is selected makes the "baseline named => 0" branch unreachable.
    setMinClicks(undefined)
    setMinClicksDraft(undefined)
  }

  const swap = () => {
    const previousBaseline = baselineNetworkUuid
    setBaselineNetworkUuid(candidateNetworkUuid)
    setCandidateNetworkUuid(previousBaseline)
    // The baseline changed, so the mode's default has to be re-applied here too.
    setMinClicks(undefined)
    setMinClicksDraft(undefined)
  }

  return (
    <>
      <Header label="Network comparison" />
      <Tabs tab={tab} setTab={setTab} suppressedCount={activeSuppressions.length} />
      {tab === 'suppressions' ? (
        <SuppressionsView
          suppressions={suppressions}
          loaded={suppressionsData !== null}
          onChanged={refetchSuppressions}
        />
      ) : (
      <Loadable data={data} placeholder={<Placeholder />}>
        <Content
          data={data}
          suppressions={activeSuppressions}
          refetchSuppressions={refetchSuppressions}
          networkList={networkList}
          baselineNetworkUuid={baselineNetworkUuid}
          candidateNetworkUuid={candidateNetworkUuid}
          setCandidateNetworkUuid={setCandidateNetworkUuid}
          changeBaseline={changeBaseline}
          swap={swap}
          minClicks={minClicksDraft}
          setMinClicks={setMinClicksDraft}
          excludeNetworkUuids={excludeNetworkUuids}
          setExcludeNetworkUuids={setExcludeNetworkUuids}
          search={search}
          setSearch={setSearch}
          sortBy={sortBy}
          setSortBy={setSortBy}
          modelGroup={modelGroup}
          setModelGroup={setModelGroup}
          retry={() => setReloadToken(reloadToken + 1)}
        />
      </Loadable>
      )}
    </>
  )
}

// The anomalies tab strip. Its classes are nested under `.anomalies-page`, which has no styles of its own.
const Tabs = (props) => {
  const { tab, setTab, suppressedCount } = props
  const items = [
    { value: 'opportunities', label: 'Opportunities' },
    { value: 'suppressions', label: `Suppressions (${suppressedCount})` },
  ]
  return (
    <div className="anomalies-page">
      <div className="anomalies-page-tabs">
        {map(items, (item) => (
          <button
            key={item.value}
            type="button"
            className={varClass({
              'anomalies-page-tab': true,
              active: tab === item.value,
            })}
            onClick={() => setTab(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// One payload feeds both: the picker options come partly from the rows, and the search has to know
// whether the result truncated before it may filter.
const Content = (props) => {
  const {
    data,
    networkList,
    baselineNetworkUuid,
    candidateNetworkUuid,
    setCandidateNetworkUuid,
    changeBaseline,
    swap,
    minClicks,
    setMinClicks,
    excludeNetworkUuids,
    setExcludeNetworkUuids,
    search,
    setSearch,
    sortBy,
    setSortBy,
    modelGroup,
    setModelGroup,
    retry,
    suppressions,
    refetchSuppressions,
  } = props

  const truncated = !!data?.truncated
  const rows = data?.rows || []

  // Rendered above the failure card AND above the results, so the prop list is written once.
  const controls = (isTruncated: boolean) => (
    <Controls
      networkOptions={networkOptions}
      baselineNetworkUuid={baselineNetworkUuid}
      candidateNetworkUuid={candidateNetworkUuid}
      setCandidateNetworkUuid={setCandidateNetworkUuid}
      changeBaseline={changeBaseline}
      swap={swap}
      minClicks={minClicks}
      setMinClicks={setMinClicks}
      excludeNetworkUuids={excludeNetworkUuids}
      setExcludeNetworkUuids={setExcludeNetworkUuids}
      search={search}
      setSearch={setSearch}
      sortBy={sortBy}
      setSortBy={setSortBy}
      modelGroup={modelGroup}
      setModelGroup={setModelGroup}
      truncated={isTruncated}
    />
  )

  // A UNION: `/networks/v1/list` omits a network deactivated since the last rebuild, which would
  // leave it visible on a row but unpickable.
  const networkOptions = useMemo(() => {
    const byUuid = new Map<string, string>()
    if (Array.isArray(networkList)) {
      for (const network of networkList) {
        byUuid.set(network.networkUuid, network.networkName)
      }
    }
    for (const network of data?.networks || []) {
      if (!byUuid.has(network.networkUuid)) {
        byUuid.set(network.networkUuid, network.networkName)
      }
    }
    return Array.from(byUuid, ([value, label]) => ({ value, label })).sort(
      (a, b) => (a.label || '').localeCompare(b.label || '')
    )
  }, [networkList, data?.networks])

  // Applied before grouping, which is what makes a sort reorder the CARDS too. Nulls sort LAST
  // explicitly, rather than relying on how JavaScript compares undefined.
  const preparedRows = useMemo(() => {
    const filtered =
      modelGroup === 'all'
        ? rows
        : rows.filter((row: any) => row.modelGroup === modelGroup)
    const field =
      sortBy === 'gap'
        ? 'rateGap'
        : sortBy === 'movable'
        ? 'baselineScoreDkk'
        : 'baselineClicks30d'
    return [...filtered].sort((a: any, b: any) => {
      const left = a[field]
      const right = b[field]
      const leftNull = left === null || left === undefined
      const rightNull = right === null || right === undefined
      if (leftNull || rightNull) return Number(leftNull) - Number(rightNull)
      return right - left
    })
  }, [rows, sortBy, modelGroup])

  // One card per domain. A Map keeps insertion order, so the ranking above survives.
  const groups = useMemo(() => {
    const byDomain = new Map<string, any[]>()
    for (const row of preparedRows) {
      const bucket = byDomain.get(row.advertiserDomain)
      if (bucket) {
        bucket.push(row)
      } else {
        byDomain.set(row.advertiserDomain, [row])
      }
    }
    return Array.from(byDomain, ([domain, domainRows]) => ({
      domain,
      rows: domainRows,
    }))
  }, [preparedRows])

  // Applied to the grouped cards, not in the request, so the server cache is unaffected by suppressions.
  // Suppressed brands still count against the 5,000-row cap.
  const [suppressedMode, setSuppressedMode] = useState<'exclude' | 'only'>(
    'exclude'
  )
  const suppressionByDomain = useMemo(
    () =>
      new Map<string, ICommissionSuppression>(
        suppressions.map((suppression: ICommissionSuppression) => [
          suppression.advertiserDomain.toLowerCase(),
          suppression,
        ])
      ),
    [suppressions]
  )
  const hiddenCount = useMemo(
    () => groups.filter((group) => suppressionByDomain.has(group.domain)).length,
    [groups, suppressionByDomain]
  )

  // Disabled when the result truncated: brands below the cut are not here, and answering "nothing
  // found" for them is a silent skip.
  const shownGroups = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const searched =
      truncated || !needle
        ? groups
        : groups.filter((group) => group.domain.includes(needle))
    return searched.filter(
      (group) =>
        suppressionByDomain.has(group.domain) === (suppressedMode === 'only')
    )
  }, [groups, search, truncated, suppressionByDomain, suppressedMode])

  const { page, setPage, maxPage, shown } = usePagination(
    { data: shownGroups, pageSize: CARDS_PER_PAGE },
    [shownGroups]
  )

  // Over `shownGroups`, not `shown`: a figure that changed as you paged would describe the page rather
  // than the comparison. "Movable" counts pairs that are actionable rather than merely comparable.
  const summary = useMemo(() => {
    let pairs = 0
    let movable = 0
    for (const group of shownGroups) {
      pairs += group.rows.length
      for (const row of group.rows) {
        if (
          row.baselineScoreDkk !== null &&
          row.baselineScoreDkk !== undefined
        ) {
          movable += 1
        }
      }
    }
    return { brands: shownGroups.length, pairs, movable }
  }, [shownGroups])

  // Digits, never a timezone conversion — which is why this is safe to print where the payload's window
  // dates are not: those arrive as UTC and a Copenhagen reader sees the day BEFORE the window.
  const builtStamp = (() => {
    const id = String(data?.builtStamp || '')
    if (id.length !== 12) return null
    return `${id.slice(0, 4)}-${id.slice(4, 6)}-${id.slice(6, 8)} ${id.slice(
      8,
      10
    )}:${id.slice(10, 12)}`
  })()

  // Three states, told apart by what was RECEIVED. A 500 in the standard envelope resolves normally and
  // arrives as `{}` with no snackbar, so a blank payload must render an explicit failure rather than an
  // empty table. The controls render ABOVE it: with only "Try again", a failure caused by the parameters
  // would be unrecoverable without a reload.
  if (data?.dataAvailable === undefined) {
    return (
      <>
        {controls(false)}
        <div className="card">
          <NoDataDiv
            title="Could not load the comparison"
            description="The request failed. Nothing is known about the data behind it — this is not an empty result."
            className="p-y-50"
          />
          <div className="text-center m-b-30">
            <button className="btn btn-primary" onClick={retry}>
              Try again
            </button>
          </div>
        </div>
      </>
    )
  }

  if (data.dataAvailable === false) {
    return (
      <div className="card">
        <NoDataDiv
          title="No comparison has been built yet"
          description="The nightly rebuild has not produced a comparison yet. It also shows here for the second or so a rebuild takes to swap the table."
          className="p-y-50"
        />
      </div>
    )
  }

  return (
    <>
      {controls(truncated)}
      {isBlank(shownGroups) ? (
        <div className="card">
          <NoDataDiv
            description={
              suppressedMode === 'only'
                ? 'Nothing is suppressed.'
                : isBlank(groups)
                ? 'No opportunities found for this comparison.'
                : hiddenCount === groups.length
                ? 'Every brand in this comparison is suppressed.'
                : 'No brand in the returned rows matches that search.'
            }
            className="p-y-50"
          />
        </div>
      ) : (
        <>
          {/* COUNTS, and deliberately no total: the same market appears in several rows at once — once
              per scope under the containment rule, once per candidate that beats the baseline — so
              summing the movable column would count the same clicks twice.

              Inline grid rather than a stylesheet class: `epc-routing`'s equivalent is scoped under its
              own page selector and its card is a module-local const. */}
          <div
            className="m-b-20"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 20,
            }}
          >
            <SummaryCard
              title="Brands"
              value={formatStat(summary.brands, 'integer')}
              extra={
                hiddenCount > 0 && suppressedMode !== 'only' ? (
                  // The pill is the button, so `.badge`'s tint pseudo-element cannot cover it.
                  <button
                    type="button"
                    className="badge text-smaller m-l-5 cursor-pointer"
                    onClick={() => setSuppressedMode('only')}
                    title="Show the suppressed brands"
                  >
                    +{formatStat(hiddenCount, 'integer')} suppressed
                  </button>
                ) : null
              }
            />
            <SummaryCard
              title="Pairs"
              value={formatStat(summary.pairs, 'integer')}
            />
            <SummaryCard
              title="Carry movable traffic"
              value={formatStat(summary.movable, 'integer')}
            />
            {/* Rendered even with no stamp, so the pager does not move when a build arrives without one. */}
            <SummaryCard
              title="Last rebuilt"
              value={builtStamp ? builtStamp.slice(0, 10) : '–'}
              sub={builtStamp ? builtStamp.slice(11) : undefined}
            />
            <SummaryCard title="Page">
              {maxPage > 1 ? (
                <Pagination {...{ page, setPage, maxPage }} />
              ) : (
                <span className="text-larger text-boldest text-dark">1</span>
              )}
            </SummaryCard>
          </div>
          {suppressedMode === 'only' && (
            <div className="card m-b-20 row row-space-between row-center">
              <span className="text-dark">
                <strong>Showing suppressed only.</strong> These brands are
                hidden from the default view until their suppression expires or
                is removed.
              </span>
              <button
                type="button"
                className="btn btn-white"
                onClick={() => setSuppressedMode('exclude')}
              >
                Back to default
              </button>
            </div>
          )}
          {/* Once, here, rather than on every card: it is a property of the RESULT. */}
          {truncated && (
            <div className="card m-b-20 text-smaller text-light">
              The result hit the 5,000-row cap and rows are ranked across all
              brands, not grouped, so a card may be missing opportunities that
              fell below the cut.
              {/* Says "its own ranking" rather than naming the movable figure, which is not on screen. */}{' '}
              Sorting reorders the returned rows only: the cut was made by the
              server on its own ranking, so a row that would rank higher here
              may be below it.
            </div>
          )}
          {map(shown, (group: any) => (
            <DomainCard
              key={group.domain}
              group={group}
              suppression={suppressionByDomain.get(group.domain) || null}
              onSuppressionChanged={refetchSuppressions}
            />
          ))}
          {/* Matches the `Page` card above. Suppressed with the pager, or an empty card is left behind. */}
          {maxPage > 1 && (
            <div className="card m-b-20">
              <div className="row row-end">
                <Pagination {...{ page, setPage, maxPage }} />
              </div>
            </div>
          )}
          {/* Page level: properties of the BUILD, unlike the CPL caveat which is about one row. The
              stronger claim that the source systematically lags is NOT made — the figure behind it
              measured the gap plus a day of growth. */}
          <div className="text-smaller text-light m-t-20">
            The comparison is rebuilt nightly: a brand that became comparable
            since the last rebuild is absent until the next one, and the
            incumbent — the network carrying the most clicks — is frozen at
            build time while EPC routing keeps moving traffic.
          </div>
        </>
      )}
    </>
  )
}

// The seven controls, in two groups. The four pickers ABOVE change the request, which is why they feed
// `useApiGet`'s deps array; the search, the sort and the model filter are applied to what came back and
// cost no round trip.
const Controls = (props) => {
  const {
    networkOptions,
    baselineNetworkUuid,
    candidateNetworkUuid,
    setCandidateNetworkUuid,
    changeBaseline,
    swap,
    minClicks,
    setMinClicks,
    excludeNetworkUuids,
    setExcludeNetworkUuids,
    search,
    setSearch,
    sortBy,
    setSortBy,
    modelGroup,
    setModelGroup,
    truncated,
  } = props

  const valueOf = (uuid) =>
    networkOptions.find((option) => option.value === uuid) || null

  const bothPickersOpen = !baselineNetworkUuid && !candidateNetworkUuid

  // The picker union is a separate async fetch and an excluded network need not appear on any row, so
  // the hardcoded defaults carry their own labels.
  const nameOf = (uuid: string) =>
    valueOf(uuid)?.label ||
    DEFAULT_EXCLUDED_NETWORKS.find((network) => network.value === uuid)
      ?.label ||
    uuid

  // Removing ONE chip, defaults included: "user-clearable" is per entry, not the whole list at once.
  const includeNetwork = (uuid: string) =>
    setExcludeNetworkUuids(
      excludeNetworkUuids.filter((other: string) => other !== uuid)
    )

  return (
    <div className="card m-b-20">
      {/* First control on the page: it is the one people reach for by name, and it filters what is
          already on screen rather than re-issuing the request. */}
      <div className="row row-bottom m-b-15">
        <div className="flex-1">
          {/* `className` overrides TableSearch's own default of `m-l-3`, a 12px left margin that is
              invisible when it sits at the right end of a row and knocks it out of line with every
              control below it here. Not `''`, which is falsy and falls back to the default. */}
          {/* The native `disabled`: an opacity class still let a keyboard user tab in and type. */}
          <TableSearch
            value={search}
            setValue={setSearch}
            className="m-l-0"
            disabled={truncated}
            placeholder={truncated ? 'Search disabled' : 'Search domain'}
            inputClassName={varClass({ 'o-50': truncated })}
          />
          {truncated && (
            <div className="text-smaller text-light m-t-3">
              The result hit the 5,000-row cap, so the domain search is
              disabled: brands below the cut are not in the page and searching
              would silently report them as missing. Narrow the comparison with
              the pickers or the minimum-clicks filter instead.
            </div>
          )}
        </div>
      </div>

      <div className="row row-space-between row-bottom">
        <div className="control control-select2 flex-1">
          <label>
            Baseline network (leave empty for the network carrying the traffic)
          </label>
          <Select
            placeholder="Any network"
            isClearable
            options={networkOptions}
            value={valueOf(baselineNetworkUuid)}
            onChange={changeBaseline}
            // The other picker's choice is disabled rather than selectable — a network compared
            // against itself yields nothing, and the page would answer "no opportunities found" to a
            // question the manager did not ask.
            isOptionDisabled={(option: any) =>
              option.value === candidateNetworkUuid
            }
          />
        </div>
        {/* Disabled when BOTH pickers are open: enabled, it re-issued the page's most expensive
            request (4.57 s) to produce the result already on screen. */}
        <button
          className="btn btn-white m-x-10"
          onClick={swap}
          disabled={bothPickersOpen}
          title={
            bothPickersOpen
              ? 'Nothing to swap while both pickers are open'
              : 'Swap baseline and candidate'
          }
        >
          Swap
        </button>
        <div className="control control-select2 flex-1">
          <label>
            Candidate network (leave empty for every network that beats it)
          </label>
          <Select
            placeholder="Any network"
            isClearable
            options={networkOptions}
            value={valueOf(candidateNetworkUuid)}
            onChange={(option: any) =>
              setCandidateNetworkUuid(option ? option.value : undefined)
            }
            isOptionDisabled={(option: any) =>
              option.value === baselineNetworkUuid
            }
          />
        </div>
      </div>

      {/* Packed left, NOT space-between: that spread them across the card once the search moved out of
          this row and left three controls to fill it. */}
      <div className="row row-bottom m-t-15">
        <div className="control">
          <label>Minimum clicks (30 days)</label>
          {/* 0 is safe in every mode: the both-open view also requires the incumbent flag, and a
              network can only BE the incumbent where clicks exist — measured, 1,676 rows against 1,678.
              `Math.max(0, ...)` clamps to the endpoint's `.min(0)`. */}
          <input
            type="number"
            min={0}
            // An empty field omits the parameter, which is what lets the backend apply its default.
            placeholder={baselineNetworkUuid ? '0' : '100'}
            value={minClicks === undefined ? '' : String(minClicks)}
            onChange={(event) => {
              const raw = event.target.value
              // Back to undefined on an empty field, or the mode default becomes unreachable for the
              // rest of the session.
              setMinClicks(
                raw === '' ? undefined : Math.max(0, parseInt(raw, 10) || 0)
              )
            }}
          />
        </div>

        {/* Beside the search, not the pickers: those re-issue the request, these do not. Not disabled
            when truncated, unlike the search — a sort reorders present rows rather than hiding absent ones. */}
        <div className="control control-select2" style={{ minWidth: 220 }}>
          <label>Sort by</label>
          <Select
            options={SORT_OPTIONS}
            value={SORT_OPTIONS.find((option) => option.value === sortBy)}
            onChange={(option: any) => setSortBy(option.value)}
          />
        </div>

        <div className="control control-select2" style={{ minWidth: 150 }}>
          <label>Model</label>
          <Select
            options={MODEL_OPTIONS}
            value={MODEL_OPTIONS.find((option) => option.value === modelGroup)}
            onChange={(option: any) => setModelGroup(option.value)}
          />
        </div>

      </div>

      {/* A CONTROL, not a hidden filter: an excluded network chosen as candidate returns "no
          opportunities found", indistinguishable from a real empty result. The Select only ADDS. */}
      <div className="row row-bottom m-t-15">
        <div
          className="control control-select2"
          style={{ minWidth: 320, maxWidth: 480 }}
        >
          <label>
            Networks excluded as candidates (not saved, restored on reload)
          </label>
          <Select
            placeholder="Add a network to exclude"
            value={null}
            options={networkOptions}
            // Unselectable rather than filtered out of `options`: a list that
            // shrinks as chips appear makes a network look like it vanished.
            isOptionDisabled={(option: any) =>
              excludeNetworkUuids.includes(option.value)
            }
            // The endpoint's `.max(50)`, enforced here so it is never
            // discovered as a 400. The hint below says why it is greyed.
            isDisabled={excludeNetworkUuids.length >= MAX_EXCLUDED_NETWORKS}
            onChange={(option: any) =>
              option &&
              setExcludeNetworkUuids([...excludeNetworkUuids, option.value])
            }
          />
          {/* Directly under the input that adds them: the chips ARE the state of that control, and
              across the card from it they read as a separate widget. */}
          <div className="m-t-5">
            {isBlank(excludeNetworkUuids) ? (
              <div className="text-smaller text-light">
                No network is excluded, so every network can be a candidate.
              </div>
            ) : (
              map(excludeNetworkUuids, (uuid: string) => (
                <span
                  key={uuid}
                  className="badge text-smaller m-r-5 inline-block m-b-3"
                >
                  {nameOf(uuid)}
                  <button
                    type="button"
                    className="link link-new-lighter text-smaller m-l-3"
                    // WITHOUT THIS THE BUTTON DOES NOT RECEIVE CLICKS. `.badge` paints its tint with an
                    // absolutely positioned `:before` covering the whole box (elements.scss), and a
                    // positioned box paints above static inline content — so the `x` sat UNDER the tint
                    // and every click hit the span, which has no handler. Positioning the button makes
                    // it a positioned sibling later in tree order, so it paints, and is hit, on top.
                    style={{ position: 'relative' }}
                    onClick={() => includeNetwork(uuid)}
                    title={`Allow ${nameOf(uuid)} as a candidate again`}
                  >
                    &times;
                  </button>
                </span>
              ))
            )}
          </div>
          {excludeNetworkUuids.length >= MAX_EXCLUDED_NETWORKS && (
            <div className="text-smaller text-light m-t-3">
              Maximum {MAX_EXCLUDED_NETWORKS} exclusions reached -- remove one
              to add another.
            </div>
          )}
        </div>
      </div>

      {/* The rate caveat has to reach the READER: a manager who sees a 75% commission and assumes the
          dashboard validated it will act on a number nobody checked. Page level, since it does not change
          from brand to brand.

          The measurement window is deliberately NOT printed — those columns arrive as UTC and the page
          would show the day BEFORE the window. The label stays "last 30d". */}
      <div className="text-smaller text-light m-t-15">
        Rates are shown exactly as each network reports them and are not
        validated or corrected here: a rate that is wrong at the network is
        reproduced faithfully.
 <strong>Potential uplift</strong> is what the
        baseline&apos;s own last-30-day traffic would have paid at the
        candidate&apos;s rate — what moving the brand would actually change, and
        nothing here prices traffic the baseline does not carry. Blank means it
        carries nothing measurable. Pending conversions count, so it is an upper
        bound: the tilde on every figure is there because it is an estimate, not
        a settled amount.
      </div>
    </div>
  )
}

// One figure, in the shape `epc-routing`'s `KpiCard` already established: same classes, same order, no
// import across features and no new stylesheet. `children` instead of a value is what lets the last card
// hold the pager rather than a number.
const SummaryCard = (props) => (
  <div className="card card-nooverflow p-x-5 p-b-4 p-t-4">
    <div className="text-light text-bold text-small m-b-2">{props.title}</div>
    {props.children || (
      <div className="text-larger text-boldest text-dark">
        {props.value}
        {props.extra}
      </div>
    )}
    {props.sub && (
      <div className="text-light text-small m-t-1">{props.sub}</div>
    )}
  </div>
)

// One card per domain. Nothing totals anything: a `nike.com` row and a `de.nike.com` row overlap.
const DomainCard = (props) => {
  const { group, suppression, onSuppressionChanged } = props
  const [opening, setOpening] = useState(false)
  const { showModal, hideModal } = useModal()
  const { showSnackbar } = useSnackbar()

  const [edit, setEdit] = useState(false)
  const widths = edit ? COLUMN_WIDTHS_EDIT : COLUMN_WIDTHS
  // Null until the first Edit, so cards nobody edits cost no request.
  const [trackers, setTrackers] = useState<ICommissionTracker[] | null>(null)
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'failed'>(
    'idle'
  )
  // Only switches that differ from the live flag, keyed by tracker uuid. Empty = nothing to apply.
  const [draft, setDraft] = useState<Record<string, boolean>>({})
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [applied, setApplied] = useState<string | null>(null)
  const [unsuppressing, setUnsuppressing] = useState(false)

  // The network name for a rate column, or null when this card's rows do not agree on one. Null is the
  // honest answer: with both pickers open the baseline is whichever network carries the traffic, and
  // that can differ between two scopes of the same brand.
  const sharedName = (key: string) => {
    const names = new Set(
      group.rows.map((row: any) => row[key]).filter(Boolean)
    )
    return names.size === 1 ? ([...names][0] as string) : null
  }
  const baselineName = sharedName('baselineNetworkName')
  const candidateName = sharedName('candidateNetworkName')

  // The tracker payload carries network uuids only.
  const networkNames = useMemo(() => {
    const names = new Map<string, string>()
    for (const row of group.rows) {
      if (row.baselineNetworkName) names.set(row.baselineNetworkUuid, row.baselineNetworkName)
      if (row.candidateNetworkName) names.set(row.candidateNetworkUuid, row.candidateNetworkName)
    }
    return names
  }, [group.rows])

  // The payload carries no advertiser uuid, because domain -> uuid is NOT 1:1. `/advs/v1/uuid` is the
  // resolver the rest of the app uses; called on click, so twenty cards cost no extra requests.
  const openMerchant = async () => {
    if (opening) return
    setOpening(true)
    try {
      const response = await getRequest(`/advs/v1/uuid/${group.domain}`)
      // Relative: an absolute host would send a local or staging session to production.
      if (response.data) window.open(`/merchants/${response.data}`, '_blank')
    } finally {
      setOpening(false)
    }
  }

  const loadTrackers = async () => {
    setLoadState('loading')
    const networkUuids = [
      ...new Set(
        group.rows.flatMap((row: any) => [
          row.baselineNetworkUuid,
          row.candidateNetworkUuid,
        ])
      ),
    ] as string[]
    const countryCodes = [
      ...new Set(group.rows.flatMap((row: any) => splitCodes(row.countryCodes))),
    ] as string[]
    const response = await fetchTrackers(group.domain, networkUuids, countryCodes)
    if (response?.data?.trackers) {
      setTrackers(response.data.trackers)
      setLoadState('idle')
    } else {
      setLoadState('failed')
    }
  }

  const toggleEdit = () => {
    if (edit) {
      setDraft({})
      setConfirming(false)
      setSaveError(null)
    } else if (trackers === null && loadState !== 'loading') {
      void loadTrackers()
    }
    setEdit(!edit)
  }

  const toggle = (tracker: ICommissionTracker) => {
    setApplied(null)
    setConfirming(false)
    setSaveError(null)
    setDraft((previous) => {
      const next = { ...previous }
      const value = !(previous[tracker.trackerUuid] ?? tracker.isPriority)
      if (value === tracker.isPriority) delete next[tracker.trackerUuid]
      else next[tracker.trackerUuid] = value
      return next
    })
  }

  const changes = (trackers || []).filter(
    (tracker) => draft[tracker.trackerUuid] !== undefined
  )

  const applyChanges = async () => {
    setSaving(true)
    setSaveError(null)
    const on = changes.filter((t) => draft[t.trackerUuid]).map((t) => t.trackerUuid)
    const off = changes.filter((t) => !draft[t.trackerUuid]).map((t) => t.trackerUuid)
    // The endpoint takes one flag value per call.
    const results = await Promise.all([
      on.length ? setTrackerPriority(on, true) : null,
      off.length ? setTrackerPriority(off, false) : null,
    ])
    setSaving(false)
    const failed = results.find((result) => result && result.code !== 200)
    if (failed) {
      // ponytail: the two calls are not atomic; on a partial failure the user is told to reload.
      setSaveError(
        `${failed.message || 'The request failed'}. Some changes may not have been applied; reload the page to see the live state.`
      )
      return
    }
    setTrackers((previous) =>
      (previous || []).map((tracker) =>
        draft[tracker.trackerUuid] === undefined
          ? tracker
          : { ...tracker, isPriority: draft[tracker.trackerUuid] }
      )
    )
    setDraft({})
    setConfirming(false)
    setApplied(
      `${changes.length} tracker${changes.length === 1 ? '' : 's'} updated. The card stays on the page until the nightly rebuild.`
    )
    showSnackbar('Tracking priority updated')
  }

  const openSuppress = () =>
    showModal(
      <SuppressModal
        domain={group.domain}
        hideModal={hideModal}
        onSuppressed={onSuppressionChanged}
      />
    )

  const unsuppress = async () => {
    setUnsuppressing(true)
    await deleteSuppression(suppression.uuid)
    setUnsuppressing(false)
    onSuppressionChanged()
  }

  return (
    <div className="card m-b-20">
      {suppression && (
        <div
          className="row row-space-between row-center m-b-10 p-x-4 p-y-2"
          style={{
            background: 'var(--new-color-orange-transparent)',
            borderRadius: 4,
          }}
        >
          <span className="text-small text-dark">
            <strong>Suppressed</strong> until {formatDate(suppression.expiresAt)}{' '}
            · “{suppression.reason}” · by {authorName(suppression)}
          </span>
          <button
            type="button"
            className="btn btn-white btn-sm"
            disabled={unsuppressing}
            onClick={unsuppress}
          >
            {unsuppressing ? 'Removing…' : 'Unsuppress'}
          </button>
        </div>
      )}

      <div className="row row-space-between row-bottom m-b-5">
        <button
          type="button"
          className="link text-bold"
          disabled={opening}
          onClick={openMerchant}
          title={`Open ${group.domain} in Merchants, in a new tab`}
        >
          {group.domain}
        </button>
        <div className="row row-center">
          {/* AssigneeList filters on `assigneeAdvertiserDomain`, which is why the payload carries it. */}
          <AssigneeList
            domain={group.domain}
            assignees={group.rows[0].assignees}
          />
          <button
            type="button"
            className="btn btn-white btn-sm m-l-10"
            onClick={toggleEdit}
            title={
              !edit
                ? 'Show a priority switch per site'
                : changes.length
                ? 'Discard the unapplied changes and hide the switches'
                : 'Hide the switches'
            }
          >
            {!edit ? 'Edit' : changes.length ? 'Discard' : 'Done'}
          </button>
          {!suppression && (
            <button
              type="button"
              className="btn btn-white btn-sm m-l-5"
              onClick={openSuppress}
              title="Hide this brand from the comparison for 30 days"
            >
              Suppress
            </button>
          )}
        </div>
      </div>

      <div className="card-body card-body-fill">
        <div className="table-container-overflow">
          {/* Widths on the HEADER cells only — the first row decides a fixed table. `minWidth` makes a
              narrow viewport SCROLL rather than clip headers mid-word; percentages have no floor. */}
          <table
            className="table table-bordered table-tight"
            style={{
              tableLayout: 'fixed',
              minWidth: 1300,
            }}
          >
            <thead>
              <tr>
                <th style={{ width: widths[0] }}>Move (from &rarr; to)</th>
                <th style={{ width: widths[1] }}>Scope</th>
                {/* Beside the rate columns rather than under the network names: CPA and CPL make those
                    three columns mean different things. */}
                <th style={{ width: widths[2] }}>Model</th>
                {/* Named after the network when this card's rows all share one, which is every row
                    whenever a picker names it and most rows otherwise, since a brand usually has one
                    incumbent. Falls back to the generic label when they differ — a header naming one
                    network above a column holding several would be a false claim. */}
                <th className="text-right" style={{ width: widths[3] }}>
                  {baselineName ? `${baselineName} rate` : 'Baseline rate'}
                </th>
                <th className="text-right" style={{ width: widths[4] }}>
                  {candidateName ? `${candidateName} rate` : 'Candidate rate'}
                </th>
                <th className="text-right" style={{ width: widths[5] }}>
                  Gap
                </th>
                {/* `clicks30d` and `scoreDkk` are in the payload but NOT rendered: they price traffic
                    the baseline does not carry, which on 84% of scored pairs is the whole figure. */}
                <th className="text-right" style={{ width: widths[6] }}>
                  Baseline clicks (30d)
                </th>
                <th className="text-right" style={{ width: widths[7] }}>
                  Potential uplift (30d)
                </th>
              </tr>
            </thead>
            <tbody>
              {map(group.rows, (row: any) => (
                <OpportunityRow
                  // The scope is in the key because a grid_row_key pair is NOT unique across the
                  // result — one card can hold two rows carrying the identical pair.
                  key={`${row.urlMatchNorm}:${row.baselineGridRowKey}:${row.candidateGridRowKey}`}
                  row={row}
                  domain={group.domain}
                  edit={edit}
                  trackers={trackers}
                  loading={loadState === 'loading'}
                  draft={draft}
                  onToggle={toggle}
                  networkNames={networkNames}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {edit && loadState === 'failed' && (
        <div className="text-smaller m-t-10" style={{ color: 'var(--text-red)' }}>
          Could not load the live trackers, so nothing can be changed here.{' '}
          <button type="button" className="link" onClick={loadTrackers}>
            Try again
          </button>
        </div>
      )}

      {edit && (
        <ChangesBar
          changes={changes}
          draft={draft}
          networkNames={networkNames}
          confirming={confirming}
          saving={saving}
          error={saveError}
          onDiscard={() => {
            setDraft({})
            setSaveError(null)
          }}
          onApply={() => setConfirming(true)}
          onBack={() => setConfirming(false)}
          onConfirm={applyChanges}
        />
      )}

      {/* The card itself only changes at the nightly rebuild, so this is the trace of the write. */}
      {applied && !changes.length && (
        <div
          className="m-t-10 p-x-4 p-y-2 text-small text-dark"
          style={{
            background: 'var(--new-color-green-transparent)',
            borderRadius: 4,
          }}
        >
          <span className="value-badge value-badge-green value-badge-sm m-r-5">
            Applied
          </span>
          {applied}
        </div>
      )}
    </div>
  )
}

// "N changes not applied yet" → Apply lists the changes → Confirm writes.
const ChangesBar = (props) => {
  const {
    changes,
    draft,
    networkNames,
    confirming,
    saving,
    error,
    onDiscard,
    onApply,
    onBack,
    onConfirm,
  } = props
  if (!changes.length) return null

  const count = `${changes.length} change${changes.length === 1 ? '' : 's'}`
  const state = (on: boolean) => (on ? 'prioritised' : 'not prioritised')
  const describe = (tracker: ICommissionTracker) =>
    `${networkNames.get(tracker.networkUuid) || tracker.networkUuid} #${
      tracker.programId || '—'
    } on ${siteName(tracker)} (${tracker.countryCode}): ${state(
      tracker.isPriority
    )} → ${state(draft[tracker.trackerUuid])}`

  return (
    <div
      className="row row-space-between row-center m-t-10 p-x-4 p-y-3"
      style={{
        background: confirming ? '#fff' : 'var(--new-color-blue-transparent)',
        border: confirming ? '1px solid var(--new-color-blue)' : undefined,
        borderRadius: 4,
      }}
    >
      {confirming ? (
        <div>
          <strong className="text-dark">
            Apply {count} to the live trackers?
          </strong>
          <ul className="text-smaller m-t-2" style={{ paddingLeft: 18 }}>
            {map(changes, (tracker: ICommissionTracker) => (
              <li key={tracker.trackerUuid}>{describe(tracker)}</li>
            ))}
          </ul>
          {error && (
            <div className="text-smaller m-t-2" style={{ color: 'var(--text-red)' }}>
              {error}
            </div>
          )}
        </div>
      ) : (
        <span className="text-dark">
          <strong>{count}</strong> not applied yet
        </span>
      )}
      <div className="row row-center text-nowrap">
        {confirming ? (
          <>
            <button
              type="button"
              className="btn btn-white btn-sm"
              disabled={saving}
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm m-l-5"
              disabled={saving}
              onClick={onConfirm}
            >
              {saving ? 'Applying…' : 'Confirm'}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-white btn-sm"
              onClick={onDiscard}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm m-l-5"
              onClick={onApply}
            >
              Apply {count}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const OpportunityRow = (props) => {
  const { row, domain, edit, trackers, loading, draft, onToggle, networkNames } =
    props
  // Edit forces the row open: the switches live under the market rows.
  const [expanded, setExpanded] = useState(false)
  const isExpanded = expanded || edit
  // Edit mode folds quiet markets (no clicks, no uplift, nothing prioritised) behind "Show all".
  const [showAllMarkets, setShowAllMarkets] = useState(false)

  const codes = splitCodes(row.countryCodes)
  const countryVolume = parseCountryVolume(row.baselineCountryVolume)

  // Restricted to the markets in the breakdown below: a badge reading "+20" over 23 prioritised markets
  // claims twenty decisions stand in the way when only three carry any of the baseline's traffic.
  const basePriority = new Set(splitCodes(row.baselinePriorityCountryCodes))
  const candidatePriority = new Set(
    splitCodes(row.candidatePriorityCountryCodes)
  )
  const carriedBasePriority = countryVolume
    .filter((volume) => basePriority.has(volume.country))
    .map((volume) => volume.country)
  const carriedCandidatePriority = countryVolume
    .filter((volume) => candidatePriority.has(volume.country))
    .map((volume) => volume.country)

  const perCountryScore = allocateScore(
    countryVolume,
    row.modelGroup,
    row.baselineScoreDkk
  )
  // Disabled rather than absent, so the first column does not shift between rows.
  const canExpand = countryVolume.length > 0 || codes.length > 0
  const carriedCountries = new Set(
    countryVolume.map((volume) => volume.country)
  )

  // The per-market rows: every country the baseline carries, PLUS any eligible market either side is
  // prioritised in but the baseline carries nothing in. Without the second part the table answers
  // "where is the traffic" but not "where is this already decided", and a market prioritised for the
  // candidate with no baseline traffic is exactly the one you would want to see. Bounded by the
  // priority sets, which are small — this is not the full eligible list.
  const eligible = new Set(codes)
  const uncarriedDecided = [...new Set([...basePriority, ...candidatePriority])]
    .filter((country) => eligible.has(country) && !carriedCountries.has(country))
    .sort()
    .map((country) => ({ country, clicks: null }))

  const isMine = (tracker: ICommissionTracker) =>
    tracker.networkUuid === row.baselineNetworkUuid ||
    tracker.networkUuid === row.candidateNetworkUuid
  const liveTrackers: ICommissionTracker[] = (trackers || []).filter(isMine)

  // Edit mode also lists eligible markets that have a tracker but no traffic or priority.
  const listed = new Set([...carriedCountries, ...uncarriedDecided.map((m) => m.country)])
  const editOnly = edit
    ? [...new Set(liveTrackers.map((tracker) => tracker.countryCode))]
        .filter((country) => eligible.has(country) && !listed.has(country))
        .sort()
        .map((country) => ({ country, clicks: null }))
    : []
  // The score travels with its market: the fold drops rows, so an index would point at the wrong one.
  const marketRows = [...countryVolume, ...uncarriedDecided, ...editOnly].map(
    (volume, index) => ({ ...volume, score: perCountryScore[index] ?? null })
  )

  // A side counts as prioritised in a market when any of its trackers there is.
  const nextOf = (tracker: ICommissionTracker) =>
    draft[tracker.trackerUuid] ?? tracker.isPriority
  const liveOn = (networkUuid: string, country: string) =>
    liveTrackers.some(
      (tracker) =>
        tracker.networkUuid === networkUuid &&
        tracker.countryCode === country &&
        nextOf(tracker)
    )
  // The nightly label until the trackers load, then the switches.
  const baseOn = (country: string) =>
    trackers ? liveOn(row.baselineNetworkUuid, country) : basePriority.has(country)
  const candidateOn = (country: string) =>
    trackers
      ? liveOn(row.candidateNetworkUuid, country)
      : candidatePriority.has(country)

  // One line per site, both networks side by side; extra programmes stack in the same cell.
  const siteLines = (country: string) => {
    const bySite = new Map<string, { channelUuid: string; name: string; baseline: ICommissionTracker[]; candidate: ICommissionTracker[] }>()
    for (const tracker of liveTrackers) {
      if (tracker.countryCode !== country) continue
      const line = bySite.get(tracker.channelUuid) || {
        channelUuid: tracker.channelUuid,
        name: siteName(tracker),
        baseline: [],
        candidate: [],
      }
      ;(tracker.networkUuid === row.baselineNetworkUuid ? line.baseline : line.candidate).push(tracker)
      bySite.set(tracker.channelUuid, line)
    }
    return [...bySite.values()].sort((a, b) => a.name.localeCompare(b.name))
  }

  // Judged on the applied state, so an unapplied change never folds its market away.
  const appliedOn = (networkUuid: string, country: string, nightly: Set<string>) =>
    trackers
      ? liveTrackers.some(
          (tracker) =>
            tracker.networkUuid === networkUuid &&
            tracker.countryCode === country &&
            tracker.isPriority
        )
      : nightly.has(country)
  const hasDraft = (country: string) =>
    liveTrackers.some(
      (tracker) =>
        tracker.countryCode === country && draft[tracker.trackerUuid] !== undefined
    )
  const isQuiet = (market) =>
    !(market.clicks > 0) &&
    market.score === null &&
    !hasDraft(market.country) &&
    !appliedOn(row.baselineNetworkUuid, market.country, basePriority) &&
    !appliedOn(row.candidateNetworkUuid, market.country, candidatePriority)
  const quietMarkets = edit ? marketRows.filter(isQuiet).length : 0
  const shownMarketRows =
    edit && !showAllMarkets ? marketRows.filter((market) => !isQuiet(market)) : marketRows

  const rate = (value: number) => formatRate(row.modelGroup, value, row.currencyCode)

  // 'pp', not '%': the gap between two percentages is percentage POINTS.
  const gap =
    row.modelGroup === 'CPA'
      ? `+${formatStat(row.rateGap, 'float3')} pp`
      : `+${formatStat(row.rateGap, 'float3')} ${
          row.currencyCode?.toUpperCase() || ''
        }`.trim()

  return (
    <>
      {/* The WHOLE row toggles; the caret stays a real button for keyboard reach and stops propagation. */}
      <tr
        className={varClass({ 'cursor-pointer': canExpand && !edit })}
        onClick={canExpand && !edit ? () => setExpanded(!expanded) : undefined}
      >
        {/* FROM -> TO, because the row describes a MOVE. */}
        <td className="text-dark">
          {/* The CONTAINER wraps, each SIDE does not, so a pair that outgrows the column breaks ONCE
              between the two networks rather than into a staircase. The arrow rides with the candidate. */}
          <div className="flex flex-items-center" style={{ flexWrap: 'wrap' }}>
            <button
              type="button"
              className="link link-new-lighter m-r-5"
              disabled={!canExpand || edit}
              onClick={(event) => {
                event.stopPropagation()
                setExpanded(!expanded)
              }}
              title={
                edit
                  ? 'Open while editing'
                  : isExpanded
                  ? 'Hide the per-market split'
                  : 'Show the per-market split'
              }
            >
              {isExpanded ? '▾' : '▸'}
            </button>
            <div className="text-nowrap">
              {row.baselineNetworkName || row.baselineNetworkUuid}
              <PriorityBadge
                codes={carriedBasePriority}
                total={countryVolume.length}
                label="now"
              />
            </div>
            <div className="text-nowrap">
              <span className="m-x-10 text-light">&rarr;</span>
              <strong>
                {row.candidateNetworkName || row.candidateNetworkUuid}
              </strong>
              <PriorityBadge
                codes={carriedCandidatePriority}
                total={countryVolume.length}
                label="preferred"
              />
            </div>
          </div>
        </td>
        <td>
          {/* Shown only when narrower than the card's domain. `.badge` is an inline-block with no break
              opportunity, so under a fixed layout a long scope grew over the Model text — hence
              `shortScope` and `overflowWrap`. */}
          {row.urlMatchNorm !== domain ? (
            <span
              className="badge text-smaller"
              style={{ maxWidth: '100%', overflowWrap: 'anywhere' }}
              title={row.urlMatch}
            >
              {shortScope(row.urlMatch, domain)}
            </span>
          ) : (
            <span className="text-light">whole domain</span>
          )}
          {/* Belongs beside the candidate's name, but cost the Move column ~60px it could not spare —
              and hiding it there saves nothing, since a fixed layout sizes for the widest state.
              Expanded only: reference data you copy when acting, not a figure you scan. */}
          {isExpanded && row.candidateImportNetworkProgramId ? (
            <div className="m-t-5">
              <span
                className="badge text-smaller text-bold"
                title={`Programme id ${
                  row.candidateImportNetworkProgramId
                } at ${row.candidateNetworkName || 'the candidate network'}`}
              >
                {row.candidateImportNetworkProgramId}
              </span>
            </div>
          ) : null}
        </td>
        <td className="text-light">{row.modelGroup}</td>
        <td className="text-right text-nowrap">{rate(row.baselineRate)}</td>
        <td className="text-right text-nowrap">{rate(row.candidateRate)}</td>
        <td className="text-right text-nowrap text-dark">
          {gap}
          {/* CPL only: those rates are flat amounts in the pair's currency while the estimate is DKK,
              so the two cannot be derived from each other. */}
          {row.modelGroup === 'CPL' && (
            <div className="text-smaller text-light text-normal text-wrap">
              Rates are in{' '}
              {row.currencyCode?.toUpperCase() || 'the network currency'}; the
              estimate is converted to DKK, so the two do not tie out.
            </div>
          )}
        </td>
        {/* An em dash, not 0: null means nothing measurable, which is not the same as zero. */}
        <td className="text-right text-nowrap">
          {row.baselineClicks30d === null || row.baselineClicks30d === undefined
            ? '—'
            : formatStat(row.baselineClicks30d, 'integer')}
        </td>
        <td className="text-right text-nowrap text-dark">
          {/* Blank, never 0: `null` is "not computed"; 0 is unreachable under the rate comparison. */}
          {uplift(row.baselineScoreDkk)}
        </td>
      </tr>

      {edit && loading && (
        <tr>
          <td colSpan={8}>
            <div className="loader-sm m-y-2" title="Loading the live trackers" />
          </td>
        </tr>
      )}
      {edit && trackers && !liveTrackers.length && (
        <tr className="text-smaller text-light">
          <td colSpan={8}>
            <span className="m-l-20">
              No active tracker of these two networks in this row&apos;s markets,
              so there is nothing to switch.
            </span>
          </td>
        </tr>
      )}

      {/* WHERE the move is — pair-level figures answer "how much", not "which markets", and
          concentration is the rule. Same table as the parent so the split reads down the same columns.
          The rate columns are EMPTY on purpose: a rate belongs to the pair, not to a market. */}
      {isExpanded &&
        shownMarketRows.map((volume) => (
          <React.Fragment key={`${volume.country}-market`}>
            <tr className="text-smaller">
              <td className="text-dark">
                <span className="m-l-20 text-bold">{volume.country}</span>
              </td>
              <td />
              <td />
              {/* UNDER THE TWO NETWORK COLUMNS, whose headers name the networks: one glance down each
                  column says where that side is already prioritised. The rate cells are empty on the pair
                  row above because a rate belongs to the pair, not to a market — which is what leaves
                  them free to carry this. */}
              <td className="text-right">
                <PriorityMark
                  on={baseOn(volume.country)}
                  title={`${
                    row.baselineNetworkName || 'The baseline'
                  } is prioritised in ${
                    volume.country
                  }, so moving this market reverses a decision someone made`}
                />
              </td>
              <td className="text-right">
                <PriorityMark
                  on={candidateOn(volume.country)}
                  title={`${
                    row.candidateNetworkName || 'The candidate'
                  } is already preferred in ${volume.country}`}
                />
              </td>
              <td />
              {/* An em dash, not 0: this market is listed because it is DECIDED, not because it carries
                  traffic, and 0 would claim the baseline was measured there and found empty. */}
              <td className="text-right text-nowrap">
                {volume.clicks === null ? (
                  <span className="text-light">&mdash;</span>
                ) : (
                  formatStat(volume.clicks, 'integer')
                )}
              </td>
              <td className="text-right text-nowrap text-dark">
                {uplift(volume.score)}
              </td>
            </tr>
            {edit &&
              trackers &&
              siteLines(volume.country)
                // No candidate tracker on the site means nothing to move to.
                .filter((line) => line.candidate.length > 0)
                .map((line) => (
                <SiteLine
                  key={`${volume.country}-${line.channelUuid}`}
                  line={line}
                  domain={domain}
                  baselineName={row.baselineNetworkName || networkNames.get(row.baselineNetworkUuid) || 'the baseline network'}
                  candidateName={row.candidateNetworkName || networkNames.get(row.candidateNetworkUuid) || 'the candidate network'}
                  nextOf={nextOf}
                  onToggle={onToggle}
                />
              ))}
          </React.Fragment>
        ))}
      {isExpanded && quietMarkets > 0 && (
        <tr className="text-smaller">
          <td colSpan={8}>
            <button
              type="button"
              className="link text-bold m-l-20"
              onClick={(event) => {
                // Hiding removes rows above the reader, so bring this card's top back into view.
                if (showAllMarkets) {
                  event.currentTarget
                    .closest('.card')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                setShowAllMarkets(!showAllMarkets)
              }}
            >
              {showAllMarkets
                ? `Hide ${quietMarkets} ${quietMarkets === 1 ? 'country' : 'countries'} without traffic or priority`
                : `Show all ${marketRows.length} countries`}
            </button>
          </td>
        </tr>
      )}
    </>
  )
}

// Locked when either side's tracker is excluded; exclusion is changed in Merchants.
const SiteLine = (props) => {
  const { line, domain, baselineName, candidateName, nextOf, onToggle } = props
  const excluded = [
    ...line.baseline.map((t: ICommissionTracker) => ({ tracker: t, network: baselineName })),
    ...line.candidate.map((t: ICommissionTracker) => ({ tracker: t, network: candidateName })),
  ].filter((entry) => entry.tracker.isExcluded)
  const locked = excluded.length > 0

  const cell = (side: ICommissionTracker[], network: string) =>
    side.length ? (
      side.map((tracker, index) => (
        <div
          key={tracker.trackerUuid}
          style={
            index > 0
              ? { borderTop: '1px solid var(--card-border)', marginTop: 6, paddingTop: 6 }
              : undefined
          }
        >
          {locked ? (
            <LockedTracker tracker={tracker} />
          ) : (
            <TrackerSwitch
              tracker={tracker}
              next={nextOf(tracker)}
              onToggle={() => onToggle(tracker)}
              label={`${network} on ${line.name}`}
            />
          )}
        </div>
      ))
    ) : (
      <span className="text-light">—</span>
    )

  return (
    <tr
      className="text-smaller"
      style={locked ? { background: 'var(--new-color-red-transparent)' } : undefined}
    >
      <td className="text-dark">
        <span className="m-l-40">{line.name}</span>
        {locked && (
          <div className="m-l-40 text-smaller text-wrap" style={{ color: 'var(--new-color-red)' }}>
            {excluded.map((entry) => (
              <div key={entry.tracker.trackerUuid}>
                {domain} from {entry.network} is manually excluded on {line.name}{' '}
                and cannot be changed here.
              </div>
            ))}
          </div>
        )}
      </td>
      <td />
      <td />
      <td className="text-right">{cell(line.baseline, baselineName)}</td>
      <td className="text-right">{cell(line.candidate, candidateName)}</td>
      <td />
      <td />
      <td />
    </tr>
  )
}

// Knob and text are siblings in a flex row: with the control's own label the meta line ran under the knob.
// The empty label still draws the knob.
const TrackerSwitch = (props) => {
  const { tracker, next, onToggle, label } = props
  const changed = next !== tracker.isPriority
  return (
    <div className="inline-block text-right m-b-1">
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span className="text-smaller text-nowrap">
          {changed && (
            <span className="m-r-3 text-bold" style={{ color: 'var(--new-color-orange)' }}>
              was {tracker.isPriority ? 'on' : 'off'}
            </span>
          )}
          {next ? <strong>Prioritised</strong> : <span className="text-light">Not prioritised</span>}
        </span>
        <div
          className="control control-switch control-focusless"
          style={{ width: 40, height: 24, flex: 'none' }}
        >
          <input type="checkbox" checked={next} onChange={onToggle} aria-label={label} />
          <label style={{ display: 'block', width: 40, height: 24, padding: 0 }} />
        </div>
      </div>
      <TrackerMeta tracker={tracker} />
    </div>
  )
}

const LockedTracker = (props) => {
  const { tracker } = props
  return (
    <div className="inline-block text-right m-b-1">
      <div className="text-smaller text-nowrap">
        {tracker.isExcluded ? (
          <span className="value-badge value-badge-red value-badge-sm">Excluded</span>
        ) : tracker.isPriority ? (
          <strong>Prioritised</strong>
        ) : (
          <span className="text-light">Not prioritised</span>
        )}
      </div>
      <TrackerMeta tracker={tracker} />
    </div>
  )
}

// Per tracker, not per line: the two networks can have different URL matches on one site.
const TrackerMeta = (props) => {
  const { tracker } = props
  return (
    <div className="text-smaller text-light">
      #{tracker.programId || '—'} · {formatRate(tracker.modelGroup, tracker.rate, tracker.currencyCode)}
      {tracker.urlMatch && (
        <span className="badge text-smaller m-l-3" title="URL match (scope)">
          {tracker.urlMatch}
        </span>
      )}
    </div>
  )
}

const siteName = (tracker: ICommissionTracker): string =>
  tracker.channelName || tracker.channelDomain || tracker.channelUuid

// Units and currency always, or a CPL pair reads as percentage points. THREE decimals, not two:
// 1,176 trackers carry a CPL flat rate in 0.00094-0.0047, which at 2 dp renders "0,00" on both sides
// with a positive gap beside it — a zero baseline that is not zero.
const formatRate = (modelGroup: string, value: number, currencyCode: string): string =>
  modelGroup === 'CPA'
    ? formatStat(value, 'percent3')
    : `${formatStat(value, 'float3')} ${currencyCode?.toUpperCase() || ''}`.trim()

// `urbanoutfitters.com/en-gb/` under its own card renders as `/en-gb/`. Stripped ONLY when the scope
// starts with the domain: for a SUBDOMAIN scope the prefix is the distinguishing part.
const shortScope = (urlMatch: string, domain: string): string => {
  if (!urlMatch || !domain) return urlMatch
  const lower = urlMatch.toLowerCase()
  return lower.startsWith(domain.toLowerCase()) &&
    lower !== domain.toLowerCase()
    ? urlMatch.slice(domain.length)
    : urlMatch
}

// `~` on every figure: this prices the baseline's own last-30-day traffic at the candidate's rate and
// counts pending conversions, so it is an estimate and an upper bound. Rounded to whole DKK for the same
// reason — decimals would claim a precision it does not have.
// '' and never 0: null is "not computed"; 0 is unreachable under the rate comparison.
const uplift = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? ''
    : `~${formatStat(Math.round(value), 'DKK', true)}`

const splitCodes = (codes: string): string[] =>
  (codes || '').split(',').filter(Boolean)

// One cell of the per-market table: prioritised, or explicitly not. "Not prioritised" is SPELLED OUT
// rather than left blank — a blank cell reads as missing data, and the whole point of this table is to
// let someone scan a column and trust what is not marked.
//
// The SAME green on both sides. The two columns are already labelled with the network names, so the
// colour has no side to distinguish — it only has to mean "prioritised".
const PriorityMark = (props) => {
  if (!props.on) {
    return <span className="text-light">Not prioritised</span>
  }

  return (
    <span
      className="value-badge value-badge-green value-badge-sm text-nowrap"
      title={props.title}
    >
      <SVG
        src="/images/advertisers/icon-priority.svg"
        width={12}
        height={12}
        className="m-r-1"
      />
      Prioritised
    </span>
  )
}

// A COUNT, not a list: "1 of 2" answers whether every market in this move is a reversal or only some.
// `label` stops the two sides reading alike — the baseline's is a status quo and green, the candidate's
// an endorsement rather than a blocker.
const PriorityBadge = (props) => {
  const codes: string[] = props.codes || []
  if (!codes.length) return null

  const isBaseline = props.label === 'now'

  return (
    <span
      // `text-nowrap`: the flex cell squeezed the count onto a second line without it.
      className={
        isBaseline
          ? 'value-badge value-badge-green value-badge-sm m-l-5 text-nowrap'
          : 'badge text-smaller m-l-5 text-nowrap'
      }
      title={`${
        isBaseline ? 'Currently prioritised in' : 'Already preferred in'
      } ${codes.join(', ')} \u2014 of the ${props.total} market${
        props.total === 1 ? '' : 's'
      } this baseline carries`}
    >
      <SVG
        src="/images/advertisers/icon-priority.svg"
        width={12}
        height={12}
        className="m-r-1"
      />
      {codes.length} of {props.total}
    </span>
  )
}

// `CC:clicks:sale_dkk:cpl_conversions`, only for countries the BASELINE carries. Re-sorted by clicks:
// the question is "where is the traffic".
const parseCountryVolume = (encoded) =>
  (encoded || '')
    .split(',')
    .filter(Boolean)
    .map((part) => {
      const [country, clicks, saleDkk, cplConversions] = part.split(':')
      return {
        country,
        clicks: Number(clicks) || 0,
        saleDkk: Number(saleDkk) || 0,
        cplConversions: Number(cplConversions) || 0,
      }
    })
    .sort((a, b) => b.clicks - a.clicks)

// ALLOCATED, never re-derived: the score is LINEAR in its volume basis, so a market's share of the basis
// is exactly its share of the score and the parts sum to the parent row. It could not be rebuilt here
// anyway — the CPL gap only exists in DKK inside the build query. `null`, never 0.
const allocateScore = (volume, modelGroup, score) => {
  const basis = (entry) =>
    modelGroup === 'CPA' ? entry.saleDkk : entry.cplConversions
  const total = volume.reduce((sum, entry) => sum + basis(entry), 0)
  if (!score || total <= 0) return volume.map(() => null)
  return volume.map((entry) => (score * basis(entry)) / total)
}
